import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';

const verificationData = [
  {
    id: 'receipts',
    label: 'Receipts Explorer',
    desc: 'Proof-of-Action logs',
    entries: [
      { hash: '0x4a7f...e91c', type: 'PoA', status: 'VERIFIED', time: '2s ago' },
      { hash: '0x8b2d...a04e', type: 'PoI', status: 'VERIFIED', time: '7s ago' },
      { hash: '0x1c5a...f73b', type: 'CBC', status: 'VERIFIED', time: '12s ago' },
    ],
  },
  {
    id: 'rwa',
    label: 'RWA Attestations',
    desc: 'On-chain asset registry',
    entries: [
      { hash: 'GPU-NV-A100-0x7f', type: 'GPU', status: 'ATTESTED', time: '1m ago' },
      { hash: 'ENERGY-ERCOT-WIN32', type: 'kWh', status: 'ATTESTED', time: '4m ago' },
      { hash: 'DATA-R-EU-GDPR-001', type: 'nDATA', status: 'ATTESTED', time: '9m ago' },
    ],
  },
  {
    id: 'audit',
    label: 'Audit Hashes',
    desc: 'Policy version control',
    entries: [
      { hash: 'sha256:9f4c2a...', type: 'Policy v2.1.4', status: 'ACTIVE', time: 'current' },
      { hash: 'sha256:3e8b1d...', type: 'Policy v2.1.3', status: 'ARCHIVED', time: '6h ago' },
      { hash: 'sha256:c720a9...', type: 'Policy v2.1.2', status: 'ARCHIVED', time: '1d ago' },
    ],
  },
  {
    id: 'controller',
    label: 'Controller Snapshot',
    desc: 'Live system state',
    entries: [
      { hash: 'NeuroOS-H v1.0.3', type: 'Runtime', status: 'HEALTHY', time: 'live' },
      { hash: 'Inference: 312ms', type: 'Latency', status: 'NOMINAL', time: 'live' },
      { hash: 'Safety: CBC-Active', type: 'Safety', status: 'LOCKED', time: 'live' },
    ],
  },
];

const statusColor = {
  VERIFIED: '#00FF88',
  ATTESTED: '#00F0FF',
  ACTIVE: '#00F0FF',
  ARCHIVED: '#555',
  HEALTHY: '#00FF88',
  NOMINAL: '#00FF88',
  LOCKED: '#FFD700',
};

export default function SecuritySection() {
  const [activeTab, setActiveTab] = useState('receipts');
  const [typedText, setTypedText] = useState('');
  const fullText = '> Initializing Proof Vault... Connecting to on-chain registry... All systems operational.';

  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      setTypedText(fullText.slice(0, i));
      i++;
      if (i > fullText.length) clearInterval(interval);
    }, 30);
    return () => clearInterval(interval);
  }, []);

  const activeData = verificationData.find((v) => v.id === activeTab);

  return (
    <section
      id="security"
      data-testid="security-section"
      className="relative py-28 md:py-36 overflow-hidden"
      style={{ background: '#000' }}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left: Text */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
          >
            <p className="font-mono text-[#00FF88] text-xs tracking-[0.35em] uppercase mb-4">
              Transparency Layer
            </p>
            <h2
              className="font-display text-white font-bold tracking-tight mb-5"
              style={{ fontSize: 'clamp(32px, 4vw, 52px)', fontFamily: "'Syne', sans-serif" }}
            >
              Live Transparency<br />
              <span className="text-[#666]">Proof Vault</span>
            </h2>
            <p className="text-[#666] text-base leading-relaxed mb-8 max-w-md">
              Every robot action generates a cryptographic receipt. Every policy change is versioned. Every RWA is attested. Nothing is hidden.
            </p>

            <div className="space-y-4">
              {[
                { icon: CheckCircle2, text: 'Receipts Explorer — browse every PoI and PoA hash' },
                { icon: CheckCircle2, text: 'RWA Attestations — on-chain GPU, energy and data registry' },
                { icon: CheckCircle2, text: 'Audit Hashes — immutable policy version history' },
                { icon: CheckCircle2, text: 'Controller Snapshot — real-time system health dashboard' },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.text} className="flex items-start gap-3">
                    <Icon size={16} color="#00FF88" className="mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-[#777]">{item.text}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Right: Terminal UI */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
          >
            <div
              className="relative"
              style={{
                background: '#080808',
                border: '1px solid rgba(0, 255, 136, 0.1)',
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              {/* Scanline effect */}
              <div className="scanline" />

              {/* Terminal header */}
              <div
                className="flex items-center gap-2 px-5 py-3 border-b"
                style={{ borderColor: 'rgba(0, 255, 136, 0.08)' }}
              >
                <div className="w-2 h-2 rounded-full bg-[#FF3B3B] opacity-70" />
                <div className="w-2 h-2 rounded-full bg-[#FFD700] opacity-70" />
                <div className="w-2 h-2 rounded-full bg-[#00FF88] opacity-70" />
                <span className="text-[#444] text-xs ml-3">neuromesh — proof-vault — zsh</span>
              </div>

              {/* Typed output */}
              <div className="px-5 py-4 border-b" style={{ borderColor: 'rgba(0, 255, 136, 0.05)' }}>
                <p className="text-xs text-[#00FF88]">
                  {typedText}
                  {typedText.length < fullText.length && <span className="terminal-cursor" />}
                </p>
              </div>

              {/* Tabs */}
              <div className="flex border-b overflow-x-auto" style={{ borderColor: 'rgba(0, 255, 136, 0.05)' }}>
                {verificationData.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setActiveTab(v.id)}
                    data-testid={`security-tab-${v.id}`}
                    className="px-4 py-3 text-xs transition-colors whitespace-nowrap"
                    style={{
                      color: activeTab === v.id ? '#00FF88' : '#444',
                      borderBottom: activeTab === v.id ? '1px solid #00FF88' : '1px solid transparent',
                      background: 'transparent',
                    }}
                  >
                    {v.label}
                  </button>
                ))}
              </div>

              {/* Content */}
              <div className="p-5 min-h-[200px]">
                <div className="text-[#444] text-xs mb-4"># {activeData.desc}</div>
                <div className="space-y-3">
                  {activeData.entries.map((entry, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.08 }}
                      className="flex items-center justify-between gap-4 py-2 border-b"
                      style={{ borderColor: 'rgba(255,255,255,0.03)' }}
                    >
                      <span className="text-xs text-[#556] font-mono truncate flex-1">{entry.hash}</span>
                      <span className="text-[10px] text-[#444]">{entry.type}</span>
                      <span
                        className="text-[10px] font-bold px-2 py-0.5 flex-shrink-0"
                        style={{
                          color: statusColor[entry.status] || '#888',
                          border: `1px solid ${statusColor[entry.status] || '#888'}30`,
                        }}
                      >
                        {entry.status}
                      </span>
                      <span className="text-[10px] text-[#333] flex-shrink-0">{entry.time}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Bottom glow */}
              <div
                className="absolute bottom-0 left-0 right-0 h-px"
                style={{ background: 'linear-gradient(90deg, transparent, rgba(0,255,136,0.3), transparent)' }}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
